import { endOfDay } from "date-fns/endOfDay";
import { startOfDay } from "date-fns/startOfDay";
import { sub } from "date-fns/sub";
import { useEffect, useState } from "react";
import LocalStorageNames from "@/constants/LocalStorageNames";

const DEFAULT_LOOKBACK_DAYS = 30;

/**
 * The reporting period, remembered across reloads.
 *
 * Reading a fleet takes minutes, so having to pick the period again after every reload is worse
 * here than on the shorter pages. Follows the same browser-local pattern the fault monitor and
 * the fuel cell page use — `usePersistedDateRange`, which stores the range per Geotab user
 * instead, is on another branch and this should move onto it once that has landed.
 */
export function useReportDateRange() {
  const [fromDate, setFromDate] = useState(() =>
    stored(
      LocalStorageNames.energyReportFromDate,
      startOfDay(sub(new Date(), { days: DEFAULT_LOOKBACK_DAYS })),
    ),
  );

  const [toDate, setToDate] = useState(() =>
    stored(LocalStorageNames.energyReportToDate, endOfDay(new Date())),
  );

  useEffect(() => {
    localStorage.setItem(LocalStorageNames.energyReportFromDate, fromDate.toISOString());
    localStorage.setItem(LocalStorageNames.energyReportToDate, toDate.toISOString());
  }, [fromDate, toDate]);

  return { fromDate, toDate, setFromDate, setToDate };
}

/**
 * The stored date, or the fallback.
 *
 * A stored value that no longer parses would reach `toISOString` in the effect above and throw a
 * `RangeError` there, taking the page down on every load until the browser storage is cleared by
 * hand — so it is checked on the way in rather than trusted.
 */
function stored(name: LocalStorageNames, fallback: Date) {
  const saved = localStorage.getItem(name);
  if (!saved) {
    return fallback;
  }
  const parsed = new Date(saved);
  return Number.isFinite(parsed.getTime()) ? parsed : fallback;
}
